import DoneIcon from "@mui/icons-material/Done";
import {
  Box,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PermissionAlert from "../../../components/Alerts/PermissionAlert";
import GlobalButton from "../../../components/CustomButton";
import appcolors from "../../../constants/Colors";
import { CURRENCY, STRIPE_KEY } from "../../../constants/Variables.js";
import { setUserData } from "../../../lib/slices/userSlice";
import { createSubscriptionHistory } from "../../../services/common/subscriptionHistoryApiFn";
import { updateCompanySubscription } from "../../../services/company/authenticationApiFn";
import { calculateRemainingDays, handleOpenSnackBar } from "../../../utils";
import { t } from "i18next";

const stripePromise = loadStripe(STRIPE_KEY);

const style = {
  cardContainer: {
    border: "2px solid #E7E9ED",
    padding: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  dollarIcon: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: appcolors.black,
    fontFamily: "GiloryBold",
  },
  price: {
    fontWeight: 600,
    fontSize: "2.5rem",
    color: appcolors.black,
    fontFamily: "GiloryBold",
  },
  duration: {
    fontWeight: 300,
    fontSize: "0.8rem",
    color: appcolors.black,
    marginLeft: 4,
  },
};

function PlansCard({ data, isLoading, setIsLoading, currentSubscription }) {
  const openAlert = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const openSubscriptionDialog = useRef(null);
  const [showChip, setShowChip] = useState(false);
  const userData = useSelector((state) => state.userData.user);
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  const remainingDays = calculateRemainingDays(currentSubscription?.endDate);

  useEffect(() => {
    setShowChip(true);
  }, [currentSubscription]);

  const handleOpenPermissionDialog = (val) => {
    openAlert.current.openDialog({
      subscription: val,
      warningText: t("planUpgradeWarning"),
    });
  };

  const handleAllowPermission = (params) => {
    navigate("/company/subscription/services", {
      state: {
        type: "company",
        user: userData?.adminId,
        subscription: {
          ...params?.subscription,
          subscriptionType: currentSubscription?.duration,
        },
      },
    });

    // localStorage.removeItem("user-data");
    // dispatch(setUserData(null));
  };

  const handleUpdateSubscription = async (subscription) => {
    try {
      setIsLoading(true);
      const response = await updateCompanySubscription(userData?.adminId, {
        subscriptionId: subscription?._id,
      });
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        const updatedData = { ...userData, ...data };
        localStorage.setItem("user-data", JSON.stringify(updatedData));
        handleOpenSnackBar("success", response.data.message);
        openAlert.current.closeDialog();
        setUserData(updatedData);
        let payload = {
          userId: data?._id,
          userType: "company",
          subscriptionId: data?.subscriptionId,
          price: subscription?.price,
          duration: "monthly",
          startDate: new Date(),
          transactionId: "12347632",
        };
        await createSubscriptionHistory(payload);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenStripeDialog = (subscription) => {
    openSubscriptionDialog.current.openDialog({
      subscription: subscription,
      type: "upgrade",
      warningText: t("alreadySubscribed"),
    });
  };

  return (
    <>
      <Grid container spacing={1} mt={3}>
        {data?.map((val, index) => {
          const active = userData?.subscriptionId === val._id;
          return (
            <Grid key={index} item xs={12} sm={6} md={4}>
              <Box sx={style.cardContainer}>
                <Box>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      variant="h3"
                      color={"#473BF0"}
                      fontSize={"0.875rem"}
                      textTransform={"uppercase"}
                    >
                      {t(val.name[currentLanguage])}
                    </Typography>
                    {active && showChip && isLoading === false && (
                      <Chip
                        sx={{
                          borderRadius: "6px",
                          backgroundColor: remainingDays
                            ? "#00c100"
                            : "#ff0000e8",
                          "& .MuiChip-label": {
                            color: "#ffffff",
                          },
                        }}
                        label={
                          remainingDays
                            ? `${remainingDays} ${t("daysLeft")}`
                            : t("expired")
                        }
                      />
                    )}
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "20px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Typography
                        mt={2}
                        variant="h2"
                        sx={[
                          style.price,
                          {
                            color:
                              currentSubscription?.subscriptionId == val._id &&
                              currentSubscription.duration == "monthly"
                                ? "#00c100"
                                : appcolors.black,
                          },
                        ]}
                      >
                        <Typography
                          component="span"
                          sx={[
                            style.dollarIcon,
                            {
                              color:
                                currentSubscription?.subscriptionId ==
                                  val._id &&
                                currentSubscription.duration == "monthly"
                                  ? "#00c100"
                                  : appcolors.black,
                            },
                          ]}
                        >
                          {CURRENCY}
                        </Typography>
                        {val.price}
                        <Typography
                          component={"span"}
                          sx={[
                            style.duration,
                            {
                              marginLeft: "3px",
                              color:
                                currentSubscription?.subscriptionId ==
                                  val._id &&
                                currentSubscription.duration == "monthly"
                                  ? "#00c100"
                                  : appcolors.black,
                            },
                          ]}
                        >
                          /{t(val.duration)}
                        </Typography>
                      </Typography>
                      {currentSubscription?.subscriptionId == val._id &&
                        currentSubscription.duration == "monthly" && (
                          <Typography
                            variant="body1"
                            fontFamily={"GiloryLight"}
                            sx={{
                              color: "#00c100",
                            }}
                          >
                            {t("active")}
                          </Typography>
                        )}
                    </Box>
                    <Box>
                      <Typography
                        mt={2}
                        variant="h2"
                        sx={[
                          style.price,
                          {
                            color:
                              currentSubscription?.subscriptionId == val._id &&
                              currentSubscription.duration == "yearly"
                                ? "#00c100"
                                : appcolors.black,
                          },
                        ]}
                      >
                        <Typography
                          component={"span"}
                          sx={[
                            style.dollarIcon,
                            {
                              color:
                                currentSubscription?.subscriptionId ==
                                  val._id &&
                                currentSubscription.duration == "yearly"
                                  ? "#00c100"
                                  : appcolors.black,
                            },
                          ]}
                        >
                          {CURRENCY}
                        </Typography>
                        {val.yearlyPrice ? val.yearlyPrice : 0}
                        <Typography
                          component="span"
                          sx={[
                            style.duration,
                            {
                              marginLeft: "3px",
                              color:
                                currentSubscription?.subscriptionId ==
                                  val._id &&
                                currentSubscription.duration == "yearly"
                                  ? "#00c100"
                                  : appcolors.black,
                            },
                          ]}
                        >
                          /{t("yearly")}
                        </Typography>
                      </Typography>
                      {currentSubscription?.subscriptionId == val._id &&
                        currentSubscription.duration == "yearly" && (
                          <Typography
                            variant="body1"
                            fontFamily={"GiloryLight"}
                            sx={{
                              color: "#00c100",
                            }}
                          >
                            {t("active")}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                  <List sx={{ m: "20px 0" }}>
                    {val.features.map((item, ind) => {
                      const listIcon = (
                        <DoneIcon
                          htmlColor="#68D585"
                          sx={{ fontSize: "1.3rem" }}
                        />
                      );

                      return (
                        <ListItem key={ind} sx={{ padding: "2px 0px" }}>
                          <ListItemIcon sx={{ minWidth: 25 }}>
                            {listIcon}
                          </ListItemIcon>
                          <ListItemText
                            sx={{
                              "& .MuiListItemText-secondary": {
                                color: "#161C2D",
                              },
                            }}
                            secondary={t(item.title[currentLanguage])}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
                <Box height={50}>
                  <GlobalButton
                    variant={"contained"}
                    btnText={active ? t("active") : t("upgrade")}
                    styleProps={{ fontWeight: 400 }}
                    handleClickBtn={() => {
                      handleOpenPermissionDialog(val);
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <PermissionAlert
        ref={openAlert}
        onAllow={(val) => {
          handleAllowPermission(val);
        }}
      />
      {/* <Elements stripe={stripePromise}>
        <SubscriptionPurchaseDialog
          ref={openSubscriptionDialog}
          // onPayment={handlePurchaseSubscriptionForSupplier}
        />
      </Elements> */}
    </>
  );
}

export default PlansCard;
